import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { alpha, Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useResizeObserverRef } from "@wojtekmaj/react-hooks";
import { useTranslation } from "react-i18next";

import { dayjs } from "@thalamos/common";
import type {
  DateOfBirth,
  Person,
  PersonInvolvement,
  PersonInvolvementKind,
  PersonName,
} from "@thalamos/haven-common";
import {
  formatPersonName,
  isInvolvementLive,
  preferredName,
} from "@thalamos/haven-common";

/**
 * The name a record may be headed with: its preferred name while that name is
 * current, and nothing once it has ended.
 *
 * A name a young person has asked not to be used is ended rather than deleted,
 * so an ended name is one there is a reason not to show. Absent is a real
 * answer here, and the only safe one.
 */
export const bannerName = (
  names: readonly PersonName[],
): string | undefined => {
  const name = preferredName(names);

  return name === undefined || name.periodEnd !== null
    ? undefined
    : formatPersonName(name);
};

/**
 * The name a young person answers to, as against the name their record is
 * labelled by.
 *
 * Named for what a reader calls it rather than after `preferredName`, which
 * ranks the official name first and so answers the opposite question.
 */
export const chosenName = (
  names: readonly PersonName[],
): string | undefined => {
  const current = names.filter((name) => name.periodEnd === null);
  const chosen =
    current.find((name) => name.use === "usual") ??
    current.find((name) => name.use === "nickname");

  return chosen === undefined ? undefined : formatPersonName(chosen);
};

/**
 * Who currently holds an involvement of this kind, and nobody once it has
 * ended.
 *
 * An ended involvement names somewhere a young person no longer is, or somebody
 * no longer answerable for them. Either shown as current would be read as a
 * fact about today.
 */
export const liveParty = (
  involvements: readonly PersonInvolvement[],
  kind: PersonInvolvementKind,
): string | undefined =>
  involvements.find(
    (involvement) =>
      involvement.kind === kind && isInvolvementLive(involvement),
  )?.party.name;

/**
 * Whole years since the date of birth, or undefined when it is not recorded.
 *
 * Nought is a real answer: a caller must test the result against `undefined`
 * and never for truthiness, or every baby reads as being of unknown age.
 */
export const ageInYears = (
  dateOfBirth: DateOfBirth | null,
): number | undefined =>
  dateOfBirth === null ? undefined : dayjs().diff(dayjs(dateOfBirth), "year");

/**
 * The width below which seven labelled values and the control stop sharing one
 * line, taken from measuring them rather than from a screen size: the row needs
 * a little under a thousand pixels for names of ordinary length, and longer ones
 * only need more.
 *
 * Compared against the row as it is laid out, so a banner in a column is judged
 * by that column and not by the monitor around it.
 */
const CARRIES_EVERY_FIELD = 1000;

/**
 * The width of the element as it is actually laid out, which is not the width
 * of the screen: a banner placed in a column is as narrow as that column on the
 * widest monitor.
 *
 * Undefined until first measured, which callers should read as "wide enough" —
 * a row that flashed collapsed would hide fields it is about to show.
 */
const useLaidOutWidth = () => {
  const [ref, entry] = useResizeObserverRef<HTMLDivElement>();

  return { ref, width: entry?.contentRect.width };
};

/**
 * What the caller actually stated, treating a blank as having stated nothing.
 * A value arriving empty says no more than an absent one, and the banner owes
 * the reader the same answer for both.
 */
const stated = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();

  return trimmed === undefined || trimmed === "" ? undefined : trimmed;
};

/**
 * One labelled value. The rule to its left separates it from the field before
 * only once the fields sit on a single line; stacked, the gap does that work.
 */
const Field = ({
  label,
  value,
  heading = false,
  held = false,
  narrow = false,
}: {
  label: string;
  value: string;
  /** Whether this value is the page's heading as well as a field of the row. */
  heading?: boolean;
  /** Whether the row is currently narrow enough for this field to be held back. */
  held?: boolean;
  /** Whether the row is stacked, in which case the gap separates rather than a rule. */
  narrow?: boolean;
}) => (
  <Box
    sx={{
      px: narrow ? 0 : 2,
      borderLeft: narrow ? "none" : "1px solid",
      borderColor: "divider",
      minWidth: 0,
      display: held ? "none" : "block",
      "&:first-of-type": { borderLeft: "none", pl: 0 },
    }}
  >
    <Typography variant="caption" color="text.secondary" component="dt" noWrap>
      {label}
    </Typography>
    <Typography
      variant="body1"
      component="dd"
      sx={{ m: 0, fontSize: "1.05rem" }}
    >
      {heading ? (
        <Box component="h1" sx={{ m: 0, font: "inherit" }}>
          {value}
        </Box>
      ) : (
        value
      )}
    </Typography>
  </Box>
);

/**
 * Who a record is about, at the head of the page showing it: what the young
 * person is called and answers to, how old they are, the legal footing they are
 * held on, the kind of care they are in and whose assessment will stand.
 *
 * The care setting says what kind of placement a young person is in and never
 * where it is. Where a looked-after child lives is frequently protected from
 * people who may otherwise know them, and a heading is legible to anyone who
 * can see the screen.
 */
export const PersonBanner = ({
  person,
  involvements = [],
  legalStatus,
  careSetting,
  onShowAllDetails,
}: {
  person: Person;
  /** Optional: an involvement the banner is not given reads as not recorded. */
  involvements?: readonly PersonInvolvement[];
  /**
   * The legal footing a young person is held on, in the words the service uses
   * for it. Stated by the caller: nothing in a record derives it.
   */
  legalStatus?: string;
  /** The kind of care a young person is in, rather than the provider of it. */
  careSetting?: string;
  /**
   * Where the rest of the record is read. The control appears only when one is
   * given, so it cannot offer a reader somewhere that does not exist.
   */
  onShowAllDetails?: () => void;
}) => {
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);
  const { ref, width } = useLaidOutWidth();

  // Only a row too narrow to carry every field has anything to expand, so the
  // control is a disclosure there and a way to the rest of the record here.
  const narrow = width !== undefined && width < CARRIES_EVERY_FIELD;
  const held = narrow && !expanded;
  // Only the narrow row ever shows itself as opened; the wide one leads away.
  // The label stays put either way: `aria-expanded` says which it is, and a name
  // that changed under the reader would be a different control to them.
  const showingAll = narrow && expanded;

  const age = ageInYears(person.dateOfBirth);
  const notRecorded = t("personListing.notRecorded");

  return (
    <Paper
      variant="outlined"
      component="header"
      ref={ref}
      sx={{
        p: 2,
        // Tinted from the palette rather than set to a colour of its own, so the
        // heading follows the brand instead of pinning one.
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
      }}
    >
      <Stack
        direction={narrow ? "column" : "row"}
        alignItems={narrow ? undefined : "center"}
        justifyContent="space-between"
        spacing={2}
      >
        <Box
          component="dl"
          sx={{
            display: narrow ? "grid" : "flex",
            gridTemplateColumns: narrow ? "repeat(2, 1fr)" : undefined,
            flexWrap: "wrap",
            gap: narrow ? 2 : 0,
            m: 0,
            minWidth: 0,
            alignItems: narrow ? undefined : "center",
          }}
        >
          <Field
            label={t("personPage.banner.fullName")}
            narrow={narrow}
            value={
              bannerName(person.names) ?? t("personPage.banner.noCurrentName")
            }
            heading
          />
          <Field
            label={t("personPage.banner.preferredName")}
            narrow={narrow}
            value={chosenName(person.names) ?? notRecorded}
          />
          <Field
            label={t("personPage.banner.age")}
            held={held}
            narrow={narrow}
            value={age === undefined ? notRecorded : String(age)}
          />
          <Field
            label={t("personPage.banner.dateOfBirth")}
            held={held}
            narrow={narrow}
            value={
              person.dateOfBirth === null
                ? notRecorded
                : dayjs(person.dateOfBirth).format("DD/MM/YYYY")
            }
          />
          <Field
            label={t("personPage.banner.legalStatus")}
            held={held}
            narrow={narrow}
            value={stated(legalStatus) ?? notRecorded}
          />
          <Field
            label={t("personPage.banner.careSetting")}
            held={held}
            narrow={narrow}
            value={stated(careSetting) ?? notRecorded}
          />
          <Field
            label={t("personPage.banner.designatedAssessor")}
            held={held}
            narrow={narrow}
            value={
              liveParty(involvements, "designated_assessor") ?? notRecorded
            }
          />
        </Box>

        {narrow || onShowAllDetails !== undefined ? (
          <Button
            variant="contained"
            aria-expanded={narrow ? expanded : undefined}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: showingAll ? "rotate(180deg)" : "none",
                  transition: "transform 150ms",
                }}
              />
            }
            onClick={
              narrow
                ? () => {
                    setExpanded((showing) => !showing);
                  }
                : onShowAllDetails
            }
            sx={{
              flexShrink: 0,
              alignSelf: narrow ? "flex-start" : "center",
            }}
          >
            {t("personPage.banner.allDetails")}
          </Button>
        ) : null}
      </Stack>
    </Paper>
  );
};
